// src/components/SaversQuickGate2025.js
import React, { useMemo, useState } from "react";

export default function SaversQuickGate() {
  const [answers, setAnswers] = useState({
    age18Plus: "",
    notDependent: "",
    notFullTimeStudent: "",
    hasEarnedIncome: "",
  });

  const handleChange = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  // 2025 simplified personal-rule pattern for teaching/demo
  const desired = useMemo(
    () => ({
      age18Plus: "yes",
      notDependent: "no",
      notFullTimeStudent: "no",
      hasEarnedIncome: "yes",
    }),
    []
  );

  const questions = useMemo(
    () => [
      { id: "age18Plus", label: "Are you at least 18 years old?" },
      { id: "notDependent", label: "Can someone else claim you as a dependent?" },
      {
        id: "notFullTimeStudent",
        label: "Were you a full-time student for 5+ months this year?",
      },
      { id: "hasEarnedIncome", label: "Do you have earned income from working?" },
    ],
    []
  );

  const allAnswered = useMemo(
    () => Object.values(answers).every((v) => v !== ""),
    [answers]
  );

  const someWrong = useMemo(() => {
    return Object.keys(answers).some((k) => answers[k] && answers[k] !== desired[k]);
  }, [answers, desired]);

  const passes = allAnswered && !someWrong;

  // ---------- STYLES ----------
  const sectionStyle = {
    borderRadius: "14px",
    padding: "1.2rem 1rem",
    backgroundColor: "#f8fafc",
    border: "1px solid #cbd5e1",
    color: "#1e293b",
    fontSize: "0.9rem",
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
  };

  const infoBoxStyle = {
    marginBottom: ".7rem",
    padding: "0.9rem",
    borderRadius: "10px",
    background: "#dce9f6",
    border: "1px solid #cbd5e1",
    color: "#0f172a",
    fontSize: "0.8rem",
    lineHeight: 1.35,
    boxSizing: "border-box",
  };

  const qCardBase = {
    padding: "0.65rem",
    borderRadius: "10px",
    transition: "0.2s ease",
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
  };

  const qTitleStyle = {
    fontWeight: 650,
    marginBottom: "0.45rem",
    color: "#0f172a",
    lineHeight: 1.25,
  };

  const radioRowStyle = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  };

  const radioPillStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    lineHeight: 1,
    whiteSpace: "nowrap",
    cursor: "pointer",
    userSelect: "none",
    padding: "8px 12px",
    borderRadius: "999px",
    border: "1px solid rgba(148,163,184,0.65)",
    background: "rgba(248,250,252,0.95)",
    color: "#334155",
    minHeight: "36px",
  };

  // ✅ Custom radio: removes the browser's outer circle entirely
  const radioBase = {
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    border: "2px solid #94a3b8",
    background: "#ffffff",
    margin: 0,
    padding: 0,
    flexShrink: 0,
    display: "inline-block",
  };

  const radioChecked = {
    background: "#2563eb",
    borderColor: "#2563eb",
    boxShadow: "inset 0 0 0 2px #ffffff",
  };

  const pillActive = {
    borderColor: "rgba(37,99,235,0.55)",
    background: "rgba(219,234,254,0.9)",
    color: "#0f172a",
  };

  const pillCorrect = {
    borderColor: "rgba(34,197,94,0.65)",
    background: "rgba(34,197,94,0.10)",
  };

  const pillWrong = {
    borderColor: "rgba(239,68,68,0.65)",
    background: "rgba(239,68,68,0.10)",
  };

  // ---------- RENDER ----------
  return (
    <div style={{ maxWidth: "480px", margin: "0 auto", width: "100%" }}>
      <section style={sectionStyle}>
        <h2 style={{ fontSize: "1rem", fontWeight: 800, marginBottom: "0.8rem" }}>
          Saver’s Credit — 2025 personal rule checker
        </h2>

        <div style={infoBoxStyle}>
          Answer all four questions to learn how the Saver’s Credit personal rules
          work for 2025.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}>
          {questions.map((q) => {
            const val = answers[q.id];
            const isAnswered = val !== "";
            const isCorrect = isAnswered && val === desired[q.id];
            const isWrong = isAnswered && val !== desired[q.id];

            let bg = "#f1f5f9";
            let border = "#cbd5e1";

            if (isCorrect) {
              bg = "rgba(34,197,94,0.18)";
              border = "rgba(34,197,94,0.7)";
            } else if (isWrong) {
              bg = "rgba(239,68,68,0.18)";
              border = "rgba(239,68,68,0.7)";
            }

            return (
              <div
                key={q.id}
                style={{
                  ...qCardBase,
                  backgroundColor: bg,
                  border: `1px solid ${border}`,
                }}
              >
                <div style={qTitleStyle}>{q.label}</div>

                <div style={radioRowStyle} role="radiogroup" aria-label={q.label}>
                  {/* YES */}
                  <label
                    style={{
                      ...radioPillStyle,
                      ...(val === "yes" ? pillActive : {}),
                      ...(isCorrect ? pillCorrect : {}),
                      ...(isWrong ? pillWrong : {}),
                    }}
                  >
                    <input
                      type="radio"
                      name={q.id}
                      value="yes"
                      checked={val === "yes"}
                      onChange={(e) => handleChange(q.id, e.target.value)}
                      style={{
                        ...radioBase,
                        ...(val === "yes" ? radioChecked : {}),
                      }}
                      aria-label="Yes"
                    />
                    <span>Yes</span>
                  </label>

                  {/* NO */}
                  <label
                    style={{
                      ...radioPillStyle,
                      ...(val === "no" ? pillActive : {}),
                      ...(isCorrect ? pillCorrect : {}),
                      ...(isWrong ? pillWrong : {}),
                    }}
                  >
                    <input
                      type="radio"
                      name={q.id}
                      value="no"
                      checked={val === "no"}
                      onChange={(e) => handleChange(q.id, e.target.value)}
                      style={{
                        ...radioBase,
                        ...(val === "no" ? radioChecked : {}),
                      }}
                      aria-label="No"
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>

        {passes && (
          <div
            style={{
              marginTop: "1rem",
              padding: "0.7rem",
              borderRadius: "10px",
              background:
                "linear-gradient(135deg, rgba(34,197,94,0.18), rgba(56,189,248,0.18))",
              border: "1px solid rgba(34,197,94,0.6)",
              fontSize: "0.85rem",
              lineHeight: 1.35,
            }}
          >
            ✔ Your answers match the 2025 example rules. Next, we’ll move into your
            income section.
          </div>
        )}

        {someWrong && (
          <div
            style={{
              marginTop: "1rem",
              padding: "0.7rem",
              borderRadius: "10px",
              background: "rgba(239,68,68,0.15)",
              border: "1px solid rgba(239,68,68,0.55)",
              fontSize: "0.85rem",
              lineHeight: 1.35,
            }}
          >
            ❌ In this educational checker, at least one personal rule doesn’t match the
            simplified 2025 pattern.
          </div>
        )}

        <p style={{ marginTop: "1rem", fontSize: "0.75rem", color: "#6b7280", lineHeight: 1.35 }}>
          {/* Optional footnote */}
        </p>
      </section>
    </div>
  );
}
